import { z } from 'zod'
import type { CheminResolu } from './acheminement'
import { libelleExercice, resoudreRacine } from './classement'
import {
  ORDRES_DOSSIER,
  type OrdreDossier,
  type Rubrique,
  type StatutDossier,
  type TypeEntite,
} from './domaine'

// Plan de classement en données : un arbre de dossiers par entité, sous sa racine de
// classement. Chaque dossier dit à quoi il sert (son rôle, lu par Claude pour placer une
// pièce) et son principe d'ordre (ce qu'il contient : ses sous-dossiers, les fichiers, ou un
// dossier par exercice ou par année). Les dossiers datés sont les seuls créés sans humain, à un
// nom calculé ; tout autre dossier est écrit, ou proposé par l'assistant puis validé, par un
// humain. Fonctions pures.

export type DossierPlan = {
  id: string
  /** Null : la racine du plan, qui est la racine de classement de l'entité. */
  parentId: string | null
  /** Nom voulu. */
  nom: string
  /** Nom des dossiers déjà créés dans le Drive ; différent du nom voulu : renommage en attente. */
  nomDrive: string
  role: string
  ordre: OrdreDossier
  precisions: string | null
  /** Sous un dossier daté : directement dessous, hors période (le dossier permanent). */
  permanent: boolean
  statut: StatutDossier
}

export type Quand = { jour: string; exerciceConcerne: number | null }

export const LIMITES_DOSSIER = { nom: 80, role: 300, precisions: 300, motif: 300 } as const

// Lecture de l'arbre

export function racineDuPlan<D extends DossierPlan>(plan: readonly D[]): D | null {
  return plan.find((d) => d.parentId === null) ?? null
}

/** Hors période d'abord (comme dans le Drive, avant les dossiers datés), puis par nom. */
const parNom = (a: DossierPlan, b: DossierPlan) =>
  Number(b.permanent) - Number(a.permanent) || a.nom.localeCompare(b.nom, 'fr')

/**
 * L'arbre en profondeur, les voisins dans l'ordre des noms (« 01 - », « 02 - »…), le
 * dossier permanent avant ceux de la période.
 */
export function ordreArbre<D extends DossierPlan>(
  plan: readonly D[],
): { dossier: D; profondeur: number }[] {
  const enfants = new Map<string | null, D[]>()
  for (const d of plan) enfants.set(d.parentId, [...(enfants.get(d.parentId) ?? []), d])
  const lignes: { dossier: D; profondeur: number }[] = []
  const vus = new Set<string>()
  const parcourir = (parent: string | null, profondeur: number) => {
    for (const d of (enfants.get(parent) ?? []).sort(parNom)) {
      if (vus.has(d.id)) continue
      vus.add(d.id)
      lignes.push({ dossier: d, profondeur })
      parcourir(d.id, profondeur + 1)
    }
  }
  parcourir(null, 0)
  return lignes
}

/** Le dossier et tout ce qu'il contient. */
export function sousArbre(plan: readonly DossierPlan[], id: string): string[] {
  const ids = [id]
  for (let i = 0; i < ids.length; i++)
    for (const d of plan) if (d.parentId === ids[i] && !ids.includes(d.id)) ids.push(d.id)
  return ids
}

/** Les dossiers en service : actifs, sous des dossiers actifs. */
export function planActif<D extends DossierPlan>(plan: readonly D[]): D[] {
  return ordreArbre(plan)
    .map((l) => l.dossier)
    .filter((d, _, tous) => {
      for (let x: D | undefined = d; x; x = tous.find((p) => p.id === x!.parentId))
        if (x.statut !== 'actif') return false
      return true
    })
}

export const periodeDe = (ordre: OrdreDossier) =>
  ordre === 'par_exercice' ? 'exercice' : ordre === 'par_annee' ? 'annee' : null

/** Un dossier reçoit des fichiers : « fichiers », ou daté sans sous-dossier dans la période. */
export function recoitFichiers(plan: readonly DossierPlan[], id: string): boolean {
  const d = plan.find((x) => x.id === id)
  if (!d) return false
  if (d.ordre === 'fichiers') return true
  if (!periodeDe(d.ordre)) return false
  return !plan.some((x) => x.parentId === id && !x.permanent)
}

/** Les dossiers où une pièce peut aller, dans l'ordre de l'arbre. */
export function dossiersTerminaux<D extends DossierPlan>(plan: readonly D[]): D[] {
  return ordreArbre(plan)
    .map((l) => l.dossier)
    .filter((d) => recoitFichiers(plan, d.id))
}

// Chemins

/**
 * Nom du dossier daté : « Exercice 2026 » (« Exercice 2025-2026 » si la clôture n'est pas au
 * 31 décembre), « Année 2026 » ; l'exercice concerné l'emporte sur la date du document. Sans
 * date : le nom type (« Exercice AAAA »).
 */
export function libellePeriode(
  ordre: 'par_exercice' | 'par_annee',
  cloture: string | null,
  quand?: Quand,
): string {
  if (!quand) return ordre === 'par_exercice' ? 'Exercice AAAA' : 'Année AAAA'
  return libelleExercice({ type: ordre === 'par_exercice' ? 'societe' : 'foyer', cloture }, quand)
}

export type Segment = { dossierId: string | null; nom: string }

/**
 * Segments sous la racine jusqu'au dossier (inclus), avec le dossier du plan que chacun
 * représente (null : un dossier daté). Noms du Drive par défaut : un renommage en attente
 * n'a pas encore eu lieu. Null : dossier introuvable.
 */
export function segmentsDuDossier(
  plan: readonly DossierPlan[],
  id: string,
  entite: { cloture: string | null },
  quand?: Quand,
  noms: 'drive' | 'voulu' = 'drive',
): Segment[] | null {
  const lignee: DossierPlan[] = []
  for (let x = plan.find((d) => d.id === id); x; x = plan.find((d) => d.id === x!.parentId)) {
    if (lignee.includes(x)) return null
    lignee.unshift(x)
  }
  if (lignee.length === 0 || lignee[0]!.parentId !== null) return null
  const segments: Segment[] = []
  lignee.forEach((x, i) => {
    if (i > 0) segments.push({ dossierId: x.id, nom: noms === 'drive' ? x.nomDrive : x.nom })
    const suivant = lignee[i + 1]
    // Le dossier daté entre ce dossier et le suivant (hors période : non) ; au bout du
    // chemin, seulement s'il reçoit les fichiers dans ses dossiers datés.
    if (periodeDe(x.ordre) && (suivant ? !suivant.permanent : recoitFichiers(plan, x.id)))
      segments.push({
        dossierId: null,
        nom: libellePeriode(x.ordre as 'par_exercice' | 'par_annee', entite.cloture, quand),
      })
  })
  return segments
}

/** Dossiers sous la racine où ranger une pièce ; null si ce dossier ne reçoit pas de fichiers. */
export function cheminDuDossier(
  plan: readonly DossierPlan[],
  id: string,
  entite: { cloture: string | null },
  quand?: Quand,
  noms: 'drive' | 'voulu' = 'drive',
): string[] | null {
  if (!recoitFichiers(plan, id)) return null
  return segmentsDuDossier(plan, id, entite, quand, noms)?.map((s) => s.nom) ?? null
}

/**
 * Chemin de rangement : la racine de l'entité (qui doit exister, jamais créée), puis les
 * dossiers du plan (créés au besoin). Null : ce dossier ne reçoit pas de fichiers.
 */
export function cheminRangement(p: {
  racine: string
  plan: readonly DossierPlan[]
  dossierId: string
  entite: { cloture: string | null }
  quand: Quand
}): CheminResolu | { erreur: string } | null {
  const existants = resoudreRacine(p.racine)
  if ('erreur' in existants) return existants
  const aCreer = cheminDuDossier(p.plan, p.dossierId, p.entite, p.quand)
  return aCreer ? { existants, aCreer } : null
}

// Règles de l'arbre

/** Comparaison des noms voisins : sans casse, accents ni espaces en trop. */
export function cleNom(nom: string): string {
  return nom.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase().replace(/\s+/g, ' ').trim()
}

const NOM_DATE = /^(exercice|annee) \d{4}(-\d{4})?$/

/** Ce qui rend le plan incohérent ; vide : il se tient. */
export function erreursPlan(plan: readonly DossierPlan[]): string[] {
  const erreurs: string[] = []
  const racines = plan.filter((d) => d.parentId === null)
  if (racines.length === 0) erreurs.push('Le plan n’a pas de racine.')
  if (racines.length > 1) erreurs.push('Le plan a plusieurs racines.')
  const parId = new Map(plan.map((d) => [d.id, d]))
  for (const d of plan) {
    if (d.parentId === null) continue
    const parent = parId.get(d.parentId)
    if (!parent) {
      erreurs.push(`« ${d.nom} » : parent introuvable.`)
      continue
    }
    const vus = new Set([d.id])
    for (let x: DossierPlan | undefined = parent; x; x = parId.get(x.parentId ?? '')) {
      if (vus.has(x.id)) {
        erreurs.push(`« ${d.nom} » : un dossier ne peut pas se contenir lui-même (boucle).`)
        break
      }
      vus.add(x.id)
    }
    if (parent.ordre === 'fichiers')
      erreurs.push(
        `« ${parent.nom || 'La racine'} » reçoit les fichiers : il ne contient pas de dossier (« ${d.nom} »).`,
      )
    const date = periodeDe(parent.ordre)
    if (d.permanent && !date)
      erreurs.push(
        `« ${d.nom} » : hors période seulement sous un dossier par exercice ou par année.`,
      )
    if (date && NOM_DATE.test(cleNom(d.nom)))
      erreurs.push(`« ${d.nom} » : nom réservé aux dossiers datés.`)
  }
  const voisins = new Map<string, number>()
  for (const d of plan)
    if (d.parentId !== null) {
      const cle = `${d.parentId}/${cleNom(d.nom)}`
      voisins.set(cle, (voisins.get(cle) ?? 0) + 1)
    }
  for (const [cle, n] of voisins)
    if (n > 1) erreurs.push(`Deux dossiers « ${cle.split('/').at(-1)} » dans le même dossier.`)
  return erreurs
}

type Modifiable = Pick<
  DossierPlan,
  'parentId' | 'nom' | 'role' | 'ordre' | 'precisions' | 'permanent'
>

/**
 * Le changement déplace des fichiers déjà rangés (autre parent, autre principe d'ordre, hors
 * période ou non) : permis seulement sur un dossier vide, sauf un découpage (`subdivise`).
 * Rôle, précisions et nom sont libres (un nom changé renomme les dossiers du Drive, sans
 * toucher aux fichiers).
 */
export function changeStructure(avant: Modifiable, modif: Partial<Modifiable>): boolean {
  return (
    (modif.parentId !== undefined && modif.parentId !== avant.parentId) ||
    (modif.ordre !== undefined && modif.ordre !== avant.ordre) ||
    (modif.permanent !== undefined && modif.permanent !== avant.permanent)
  )
}

/**
 * Seule exception, permise sur un dossier qui contient des pièces : découper en sous-dossiers,
 * sur place, un dossier qui reçoit les fichiers. Ses pièces repartent au placement (Claude les
 * place dans les nouveaux sous-dossiers, le fichier suit dans le Drive) ; un humain classe
 * celles où il hésite.
 */
export function subdivise(avant: Modifiable, modif: Partial<Modifiable>): boolean {
  return (
    avant.ordre === 'fichiers' &&
    modif.ordre === 'sous_dossiers' &&
    (modif.parentId === undefined || modif.parentId === avant.parentId) &&
    (modif.permanent === undefined || modif.permanent === avant.permanent)
  )
}

// Plan de départ : le plan de classement tenu jusqu'ici, avec un rôle pour chaque dossier.

export type NoeudDepart = {
  cle: string
  parent: string | null
  nom: string
  role: string
  ordre: OrdreDossier
  precisions: string | null
  permanent: boolean
  /** Rubriques de la lecture qu'il reçoit : sert à reprendre les pièces déjà classées. */
  rubriques: Rubrique[]
}

const fichiers = (
  cle: string,
  parent: string,
  nom: string,
  role: string,
  rubriques: Rubrique[],
): NoeudDepart => ({
  cle,
  parent,
  nom,
  role,
  ordre: 'fichiers',
  precisions: null,
  permanent: false,
  rubriques,
})

const PERMANENT_SOCIETE: NoeudDepart[] = [
  {
    cle: 'permanent',
    parent: 'racine',
    nom: '1 - Dossier permanent',
    role: 'Ce qui vaut d’un exercice à l’autre : statuts, contrats, baux, prêts, actes.',
    ordre: 'sous_dossiers',
    precisions: null,
    permanent: true,
    rubriques: [],
  },
  fichiers(
    'juridique',
    'permanent',
    '1.1 - Juridique',
    'Statuts, Kbis, procès-verbaux et décisions d’associés (hors approbation des comptes), cessions de parts, convention d’indivision, registres.',
    ['juridique'],
  ),
  fichiers(
    'contrats',
    'permanent',
    '1.2 - Contrats et assurances',
    'Le contrat lui-même (lettre de mission, assurance, abonnement, mandat de gestion, contrat de service), pas ses factures.',
    ['contrats'],
  ),
  fichiers(
    'baux',
    'permanent',
    '1.3 - Baux',
    'Bail, avenant, état des lieux, dépôt de garantie, caution.',
    ['baux'],
  ),
  fichiers(
    'financements',
    'permanent',
    '1.4 - Emprunts et financements',
    'Offre et contrat de prêt, tableau d’amortissement, garantie, nantissement.',
    ['financements'],
  ),
  fichiers(
    'patrimoine',
    'permanent',
    '1.5 - Patrimoine',
    'Acte d’achat ou de vente, titre de propriété, diagnostics, plans, règlement de copropriété.',
    ['patrimoine'],
  ),
]

const EXERCICE_SOCIETE: NoeudDepart[] = [
  fichiers(
    'ventes',
    'racine',
    '01 - Ventes et loyers',
    'Factures et avoirs émis, appels de loyer, quittances, comptes rendus de gérance.',
    ['ventes'],
  ),
  fichiers(
    'achats',
    'racine',
    '02 - Achats et frais',
    'Factures et avoirs de fournisseurs pour des frais courants : énergie, honoraires, abonnements, primes d’assurance, frais bancaires, fournitures.',
    ['achats'],
  ),
  fichiers(
    'banque',
    'racine',
    '03 - Banque',
    'Relevés de compte, avis d’opération, échéancier annuel d’un prêt.',
    ['banque'],
  ),
  fichiers(
    'fiscal',
    'racine',
    '04 - Fiscal',
    'TVA, IS, CFE, taxe foncière : avis, déclarations, accusés de dépôt, courriers de l’administration fiscale.',
    ['fiscal'],
  ),
  fichiers(
    'social',
    'racine',
    '05 - Social',
    'Bulletins de paie, DSN, URSSAF, retraite, prévoyance.',
    ['social'],
  ),
  fichiers(
    'travaux',
    'racine',
    '06 - Travaux et immobilisations',
    'Devis signés et factures de travaux, de réparation d’un bien, d’équipement ou d’immobilisation.',
    ['travaux'],
  ),
  fichiers(
    'courriers',
    'racine',
    '07 - Courriers et litiges',
    'Courriers divers, relances, mises en demeure, sinistres, litiges, convocations.',
    ['courriers'],
  ),
  fichiers(
    'cloture',
    'racine',
    '08 - Clôture',
    'Comptes annuels, bilan, liasse, procès-verbal d’approbation des comptes, documents de clôture du cabinet.',
    ['cloture'],
  ),
]

const PLAN_SOCIETE: NoeudDepart[] = [
  {
    cle: 'racine',
    parent: null,
    nom: '',
    role: 'Toutes les pièces de l’entité, comme un dossier de révision : le dossier permanent, puis un dossier par exercice découpé par cycle.',
    ordre: 'par_exercice',
    precisions:
      'Exercice nommé par l’année de clôture ; l’exercice concerné l’emporte sur la date du document (liasse 2025 reçue en 2026 : Exercice 2025).',
    permanent: false,
    rubriques: [],
  },
  ...PERMANENT_SOCIETE,
  ...EXERCICE_SOCIETE,
]

const PLAN_FOYER: NoeudDepart[] = [
  {
    cle: 'racine',
    parent: null,
    nom: '',
    role: 'Les papiers du foyer : le dossier permanent, puis un dossier par année civile.',
    ordre: 'par_annee',
    precisions:
      'Année des revenus ou de la période concernée, sinon celle du document (avis 2026 sur les revenus 2025 : Année 2025).',
    permanent: false,
    rubriques: [],
  },
  {
    cle: 'permanent',
    parent: 'racine',
    nom: '1 - Dossier permanent',
    role: 'Ce qui vaut plusieurs années : identité, contrats, prêts, patrimoine.',
    ordre: 'sous_dossiers',
    precisions: null,
    permanent: true,
    rubriques: [],
  },
  fichiers(
    'etat_civil',
    'permanent',
    '1.1 - État civil et identité',
    'Pièces d’identité, livret de famille, actes d’état civil.',
    ['etat_civil'],
  ),
  fichiers(
    'contrats',
    'permanent',
    '1.2 - Contrats et assurances',
    'Contrats et polices d’assurance, bail du logement, abonnements : le contrat, pas ses factures.',
    ['contrats', 'baux'],
  ),
  fichiers(
    'financements',
    'permanent',
    '1.3 - Emprunts',
    'Offres et contrats de prêt, tableaux d’amortissement.',
    ['financements'],
  ),
  fichiers(
    'patrimoine',
    'permanent',
    '1.4 - Patrimoine',
    'Actes d’achat ou de vente, titres de propriété, diagnostics.',
    ['patrimoine'],
  ),
  fichiers(
    'fiscal',
    'racine',
    '01 - Impôts',
    'Impôt sur le revenu, prélèvement à la source, taxe foncière, taxe d’habitation : avis, déclarations, courriers.',
    ['fiscal'],
  ),
  fichiers(
    'revenus',
    'racine',
    '02 - Revenus',
    'Salaires, attestations, IFU, relevés de revenus.',
    ['revenus'],
  ),
  fichiers(
    'sante',
    'racine',
    '03 - Santé',
    'Ameli, mutuelle : remboursements, attestations, factures de santé.',
    ['sante'],
  ),
  fichiers(
    'famille',
    'racine',
    '04 - Famille',
    'CAF, crèche, école, garde d’enfants, Pajemploi (le foyer employeur).',
    ['famille', 'social'],
  ),
  fichiers(
    'logement',
    'racine',
    '05 - Logement',
    'Énergie, eau, internet, assurance habitation, charges et travaux du domicile.',
    ['logement', 'travaux'],
  ),
  fichiers(
    'banque',
    'racine',
    '06 - Banque et placements',
    'Relevés de compte, placements, avis d’opération.',
    ['banque'],
  ),
  fichiers(
    'achats',
    'racine',
    '07 - Achats et garanties',
    'Factures d’achat à garder, garanties.',
    ['achats'],
  ),
  fichiers(
    'courriers',
    'racine',
    '08 - Courriers',
    'Courriers divers, relances, litiges, convocations.',
    ['courriers'],
  ),
]

/** Plan de départ d'une nouvelle entité : celui du foyer, ou celui des sociétés (LMNP compris). */
export function planDeDepart(type: TypeEntite): NoeudDepart[] {
  return type === 'foyer' ? PLAN_FOYER : PLAN_SOCIETE
}

// Saisie

const nomDossier = z
  .string()
  .trim()
  .min(1)
  .max(LIMITES_DOSSIER.nom)
  .refine((n) => !n.includes('/') && n !== '.' && n !== '..', {
    error: 'Nom de dossier sans « / », ni « . » ou « .. ».',
  })
const role = z.string().trim().min(1, 'Dire à quoi sert le dossier.').max(LIMITES_DOSSIER.role)
const precisions = z.string().trim().max(LIMITES_DOSSIER.precisions)
/** Pourquoi : demandé à l'assistant quand il propose, gardé avec la proposition. */
const motif = z.string().trim().min(1).max(LIMITES_DOSSIER.motif)

const videEnNul = (t: string | null) => (t === null || t === '' ? null : t)

/** Les mots d'un humain qui demande à l'assistant de créer ou valider un dossier. */
export const demandeHumaine = z.string().trim().min(3).max(300)

export const dossierCreation = z.object({
  parentId: z.uuid(),
  nom: nomDossier,
  role,
  ordre: z.enum(ORDRES_DOSSIER).default('fichiers'),
  precisions: precisions.nullable().default(null).transform(videEnNul),
  permanent: z.boolean().default(false),
  motif: motif.optional(),
  /**
   * Par l'assistant, les mots de la demande explicite d'un humain dans la conversation : le
   * dossier est créé en service, sans proposition (décision de Pierre-Marie du 11/10/2026).
   */
  demande: demandeHumaine.optional(),
})
export type DossierCreation = z.infer<typeof dossierCreation>

export const dossierModification = z
  .object({
    parentId: z.uuid().optional(),
    nom: nomDossier.optional(),
    role: role.optional(),
    ordre: z.enum(ORDRES_DOSSIER).optional(),
    precisions: precisions
      .nullable()
      .optional()
      .transform((t) => (t === undefined ? undefined : videEnNul(t))),
    permanent: z.boolean().optional(),
    motif: motif.optional(),
  })
  .refine(({ motif: _, ...champs }) => Object.values(champs).some((v) => v !== undefined), {
    error: 'Rien à modifier.',
  })
  // Un champ absent ne change rien : il n'apparaît pas (null, lui, vide les précisions).
  .transform(
    (m) =>
      Object.fromEntries(
        Object.entries(m).filter(([, v]) => v !== undefined),
      ) as DossierModification,
  )
export type DossierModification = {
  parentId?: string
  nom?: string
  role?: string
  ordre?: OrdreDossier
  precisions?: string | null
  permanent?: boolean
  motif?: string
}

/** Changement en attente sur un dossier actif, proposé par l'assistant. */
export type PropositionDossier = Omit<DossierModification, 'motif'> & { supprimer?: true }

/**
 * Le motif d'un placement dit par Claude cite les dossiers par leur étiquette (« D27 », « P1 ») :
 * chacune est remplacée par le nom du dossier, entre guillemets, pour être lue par un humain.
 * Une étiquette inconnue reste telle quelle.
 */
export function motifSansEtiquettes(motif: string, noms: ReadonlyMap<string, string>): string {
  return motif.replace(/(?<![\p{L}\p{N}])[DP]\d+(?![\p{L}\p{N}])/gu, (e) => {
    const nom = noms.get(e)
    return nom === undefined ? e : `« ${nom} »`
  })
}
